import { get, invert, keyBy } from 'lodash';
import { matchPath } from 'react-router-dom';
import type { ParametricSelector, Selector } from 'reselect';
import { filter } from 'rxjs/operators';
import Url from 'url-parse';

import type { Brand, RoutingKey } from '@swf/core/enums';
import { Vertical } from '@swf/core/enums';
import type {
  EffectParams,
  ModelConfig,
  RootState,
  SelectorCreator,
  Slicer
} from '@swf/core/store';
import { select } from '@swf/core/store';
import type { Layout } from '@swf/core/types/Layout';
import { canUseDOM } from '@swf/core/utils';
import { flattenWidgets } from '@swf/core/utils/flattenWidgets';

import type { WidgetContainer } from '../types/Widget';
import { fetchProperties } from '../utils/widgets';

import type { LocaleState } from './locale';
import { defaultModel } from './widget';

export interface PageMeta {
  PWATitle: string;
  activePath: string;
  authRequired?: boolean;
  baseURL: string;
  description: string;
  noIndex?: boolean;
  favicon?: string;
  redirect?: { statusCode?: number; location: string };
  routes: { [key: string]: string };
  source?: string;
  title: string;
  titleTemplate?: string;
  tokens: Record<LocaleState, Record<string, unknown>>;
  trackingId: string;
  vertical: Vertical;
  brand?: Brand;
}

export interface PageState {
  layout?: Layout;
  meta?: PageMeta;
}

interface PageModel extends ModelConfig<PageState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

interface RequestBody {
  path: string;
  siteId?: string;
}

const templateCache: Record<string, PageState | undefined> = {};

const reducers = {
  response: (state: PageState, payload: PageState) => payload
};

const effects = {
  async loadWidget(widget: WidgetContainer, { actions, dispatch, addModel }: EffectParams) {
    const { model = defaultModel } = await fetchProperties(widget.type);
    const state = get(canUseDOM && window, ['__INITIAL_STATE__', widget.id], model.state);
    const unmount = actions.pipe(
      filter(
        ({ payload, type }) =>
          type === 'page/response' &&
          (!payload.layout || !flattenWidgets(payload.layout).find((item) => item.id === widget.id))
      )
    );

    addModel({ ...model, state, name: widget.id, widget: { ...widget, unmount } });

    if (canUseDOM && window.__INITIAL_STATE__) delete window.__INITIAL_STATE__[widget.id];
    const preload = dispatch[widget.id].preload?.().catch(() => undefined);
    if (!canUseDOM) await preload;
  },
  async preload(layout: Layout | undefined, params: EffectParams) {
    let widgets: WidgetContainer[];
    const current = select.page.get(params.rootState)?.layout;

    if (current && layout) {
      const currentWidgets = keyBy(flattenWidgets(current), 'id');
      widgets = flattenWidgets(layout).filter(({ id }) => !currentWidgets[id]);
    } else {
      layout = layout || current;
      if (!layout) return;
      widgets = flattenWidgets(layout);
    }
    await Promise.all(widgets.map((widget) => effects.loadWidget(widget, params)));
  },
  async fetchPage({ href, version }: { href: string; version?: string }, params: EffectParams) {
    const { rootState, dispatch, request } = params;
    const location = select.location.get(rootState);
    const url = new Url(href, true);
    const { pathname, query } = url;
    const siteId = query.siteId || location.query.siteId;
    const payload = {
      service: 'cms.template',
      body: {
        path: select.location.stripLocale(rootState, pathname),
        clientVersion: canUseDOM ? window.version : version
      }
    };

    if (siteId) {
      (payload.body as RequestBody).siteId = siteId;
      url.set('query', { ...query, siteId });
    }

    const cacheKey = url.toString();
    let template: PageState | undefined;

    if (canUseDOM) {
      template = templateCache[cacheKey];
    }

    if (!template) {
      const { body } = await request<PageState>(payload);
      template = body;
    }

    if (canUseDOM) {
      templateCache[cacheKey] = template;
    }

    await effects.preload(template.layout, params);
    dispatch.page.response(template);
  }
};

const selectors = (slice: Slicer<PageState>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  getActiveRoutingKey() {
    return slice(({ meta }) => {
      if (meta) {
        const match = Object.entries(meta.routes).find(([_key, path]) => path === meta.activePath);
        return match && match[0];
      }
    });
  },
  getVertical() {
    return slice((state) => state.meta?.vertical || Vertical.Global);
  },
  getVerticalName(this: { getVertical: Selector<RootState, Vertical> }) {
    return createSelector(this.getVertical, (vertical) => invert(Vertical)[vertical]);
  },
  getMeta() {
    return slice((state) => (state.meta || {}) as Partial<PageMeta>);
  },
  getFavicon() {
    return slice((state) => state.meta?.favicon);
  },
  getWidget() {
    return createSelector(
      slice,
      (rootState: RootState, id: string | undefined) => id,
      ({ layout }, id) =>
        layout
          ? flattenWidgets(layout).find((widget) => widget.id === id || widget.type === id)
          : undefined
    );
  },
  matchRoute(this: { getMeta: Selector<RootState, Partial<PageMeta>> }) {
    return createSelector(
      (rootState) => select.location.stripLocale(rootState, undefined),
      this.getMeta,
      (rootState: RootState, key: RoutingKey) => key,
      (pathname, { routes }, key) =>
        !!(routes && matchPath(pathname, { path: routes[key], exact: true, strict: false }))
    );
  },
  getMatchingRoute(this: { matchRoute: ParametricSelector<RootState, RoutingKey, boolean> }) {
    return createSelector(
      (rootState: RootState, routingKeys: RoutingKey[]) => ({ rootState, routingKeys }),
      ({ rootState, routingKeys }) => routingKeys.find((key) => this.matchRoute(rootState, key))
    );
  },
  isAuthRequired() {
    return slice((state) => !!state.meta?.authRequired);
  },
  isSportsVertical() {
    return slice((state) => state.meta?.vertical === Vertical.Sports);
  },
  isStatic() {
    return slice((state) => state.meta?.source === 'cms');
  }
});

const page: PageModel = {
  state: {},
  reducers,
  effects,
  selectors
};

export default page;
